import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FileCheck, Clock, XCircle, Upload } from "lucide-react";

const BUSINESS_TYPES = [
  { value: "individual", label: "Individual / Sole Proprietor" },
  { value: "proprietorship", label: "Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "pvt-ltd", label: "Private Limited" },
  { value: "llp", label: "LLP" },
];

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const STATUS_MAP = {
  pending: { label: "Not started", icon: Clock, color: "text-muted-foreground", bg: "bg-muted" },
  submitted: { label: "Under review", icon: FileCheck, color: "text-warning", bg: "bg-warning/10" },
  verified: { label: "Verified", icon: ShieldCheck, color: "text-success", bg: "bg-success/10" },
  rejected: { label: "Rejected", icon: XCircle, color: "text-destructive", bg: "bg-destructive/10" },
};

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function KycPage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    business_type: "individual",
    gst_number: "",
    pan_number: "",
    aadhaar_number: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusDetails, setStatusDetails] = useState(null);

  useEffect(() => {
    if (user?.kyc_status && user.kyc_status !== "pending") {
      api.get("/kyc/status").then((r) => setStatusDetails(r.data)).catch(() => {});
    }
    if (user?.kyc) {
      setForm((f) => ({
        ...f,
        business_name: user.kyc.business_name || f.business_name,
        business_type: user.kyc.business_type || f.business_type,
        gst_number: user.kyc.gst_number || "",
        pan_number: user.kyc.pan_number || "",
        aadhaar_number: user.kyc.aadhaar_number || "",
        address_line1: user.kyc.address_line1 || "",
        address_line2: user.kyc.address_line2 || "",
        city: user.kyc.city || "",
        state: user.kyc.state || "",
        pincode: user.kyc.pincode || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.kyc_status]);

  const onChange = (k) => (e) =>
    setForm({ ...form, [k]: typeof e === "string" ? e : e.target.value });

  const handleFiles = async (ev) => {
    const files = Array.from(ev.target.files || []);
    const mapped = [];
    for (const f of files) {
      if (f.size > 2 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 2MB`);
        continue;
      }
      mapped.push({ name: f.name, data_url: await fileToDataUrl(f) });
    }
    setDocuments((d) => [...d, ...mapped]);
    ev.target.value = "";
  };

  const removeDoc = (i) => setDocuments((d) => d.filter((_, idx) => idx !== i));

  const validate = () => {
    const errs = [];
    if (!form.business_name.trim()) errs.push("Business name required");
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number)) errs.push("Invalid PAN (format: ABCDE1234F)");
    if (!/^\d{12}$/.test(form.aadhaar_number)) errs.push("Invalid Aadhaar (12 digits)");
    if (!form.address_line1.trim()) errs.push("Address line 1 required");
    if (!form.city.trim()) errs.push("City required");
    if (!form.state) errs.push("State required");
    if (!/^\d{6}$/.test(form.pincode)) errs.push("Invalid pincode");
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    setLoading(true);
    try {
      await api.post("/kyc/submit", { ...form, documents });
      toast.success("KYC submitted. Our team will review within 24 hours.");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "KYC submission failed");
    } finally {
      setLoading(false);
    }
  };

  const status = user?.kyc_status || "pending";
  const S = STATUS_MAP[status];
  const locked = status === "submitted" || status === "verified";

  return (
    <DashboardLayout>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Partner Onboarding</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              KYC &amp; Business Details
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              We need a few details to enable BBPS and payout processing on your partner account.
              All data is encrypted and handled per RBI guidelines.
            </p>
          </div>
          <div className={`rounded-md border border-border p-3 flex items-center gap-3 ${S.bg}`}>
            <S.icon className={`h-5 w-5 ${S.color}`} strokeWidth={1.5} />
            <div>
              <div className="overline">Status</div>
              <div className={`font-semibold ${S.color}`} data-testid="kyc-status-label">
                {S.label}
              </div>
            </div>
          </div>
        </div>

        {statusDetails?.kyc?.remarks && status === "rejected" && (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4">
            <div className="overline text-destructive">Reviewer remarks</div>
            <p className="text-sm mt-1">{statusDetails.kyc.remarks}</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-8 space-y-8" data-testid="kyc-form">
          <section className="surface-card p-6">
            <div className="overline">Business</div>
            <div className="grid sm:grid-cols-2 gap-5 mt-4">
              <div>
                <Label>Business name</Label>
                <Input
                  data-testid="kyc-business-name"
                  value={form.business_name}
                  onChange={onChange("business_name")}
                  disabled={locked}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Business type</Label>
                <Select
                  value={form.business_type}
                  onValueChange={(v) => setForm({ ...form, business_type: v })}
                  disabled={locked}
                >
                  <SelectTrigger className="mt-1" data-testid="kyc-business-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST number (optional)</Label>
                <Input
                  data-testid="kyc-gst"
                  value={form.gst_number}
                  onChange={onChange("gst_number")}
                  disabled={locked}
                  className="mt-1 font-mono"
                  placeholder="27ABCDE1234F1Z5"
                />
              </div>
            </div>
          </section>

          <section className="surface-card p-6">
            <div className="overline">Identity</div>
            <div className="grid sm:grid-cols-2 gap-5 mt-4">
              <div>
                <Label>PAN number</Label>
                <Input
                  data-testid="kyc-pan"
                  value={form.pan_number}
                  onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
                  disabled={locked}
                  className="mt-1 font-mono"
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
              <div>
                <Label>Aadhaar number</Label>
                <Input
                  data-testid="kyc-aadhaar"
                  value={form.aadhaar_number}
                  onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value.replace(/\D/g, "") })}
                  disabled={locked}
                  className="mt-1 font-mono"
                  placeholder="12-digit Aadhaar"
                  maxLength={12}
                />
              </div>
            </div>
          </section>

          <section className="surface-card p-6">
            <div className="overline">Registered address</div>
            <div className="grid sm:grid-cols-2 gap-5 mt-4">
              <div className="sm:col-span-2">
                <Label>Address line 1</Label>
                <Input
                  data-testid="kyc-address1"
                  value={form.address_line1}
                  onChange={onChange("address_line1")}
                  disabled={locked}
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Address line 2 (optional)</Label>
                <Input
                  data-testid="kyc-address2"
                  value={form.address_line2}
                  onChange={onChange("address_line2")}
                  disabled={locked}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  data-testid="kyc-city"
                  value={form.city}
                  onChange={onChange("city")}
                  disabled={locked}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select
                  value={form.state}
                  onValueChange={(v) => setForm({ ...form, state: v })}
                  disabled={locked}
                >
                  <SelectTrigger className="mt-1" data-testid="kyc-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pincode</Label>
                <Input
                  data-testid="kyc-pincode"
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })}
                  disabled={locked}
                  className="mt-1 font-mono"
                  maxLength={6}
                />
              </div>
            </div>
          </section>

          <section className="surface-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Documents</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload PAN card, Aadhaar front/back, business proof (optional but recommended).
                  PNG/JPG/PDF, max 2MB each.
                </p>
              </div>
              {!locked && (
                <label
                  htmlFor="doc-upload"
                  data-testid="kyc-upload-trigger"
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                >
                  <Upload className="h-4 w-4" strokeWidth={1.5} />
                  Upload files
                  <input
                    id="doc-upload"
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleFiles}
                  />
                </label>
              )}
            </div>
            {documents.length > 0 && (
              <ul className="mt-4 divide-y divide-border border border-border rounded-md">
                {documents.map((d, i) => (
                  <li
                    key={`${d.name}-${i}`}
                    className="flex items-center justify-between px-4 py-2 text-sm"
                  >
                    <span className="truncate">{d.name}</span>
                    {!locked && (
                      <button
                        type="button"
                        onClick={() => removeDoc(i)}
                        className="text-xs text-destructive hover:underline"
                        data-testid={`remove-doc-${i}`}
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {!locked && (
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="font-normal">
                Submitting locks your details for review.
              </Badge>
              <Button
                type="submit"
                size="lg"
                className="tracking-wide"
                disabled={loading}
                data-testid="submit-kyc"
              >
                {loading ? "Submitting…" : "Submit for review"}
              </Button>
            </div>
          )}
        </form>
      </div>
    </DashboardLayout>
  );
}
